/**
 * Turning curriculum prose into search-result prose.
 *
 * Titles and descriptions are *written* by the content, not by this file — a
 * lesson's summary is already a sentence addressed to a student, which is
 * exactly what a search result should say. What is left is arithmetic: fit it
 * in the space a result gives you, and never cut a word in half.
 */

import { SITE } from './site.ts';

/**
 * Roughly what Google shows before it truncates. Both numbers are soft limits
 * — nothing breaks past them, the tail is simply not read by anyone.
 */
export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 158;

/** Collapses authored whitespace so a multi-line summary fits one line. */
export function flatten(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Truncates at a word boundary, appending an ellipsis.
 *
 * Cutting mid-word is the tell of a generated description, and a description
 * that looks generated is one nobody clicks.
 */
export function truncate(value: string, max: number): string {
  const flat = flatten(value);
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const body = (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.—-]+$/, '');
  return `${body}…`;
}

/**
 * Appends the brand to a page title, unless the title is already about the
 * brand — "Yuan Math — Yuan Math" is how a template announces itself.
 *
 * When both will not fit, **the brand goes and the title stays**. A lesson
 * called "Greatest Common Factor and Lowest Common Multiple" is exactly the
 * phrase somebody searched for; cutting it to "…and Lowest Common" so that a
 * brand nobody searched for survives is the wrong half to keep. The title is
 * only ever truncated when it is too long on its own.
 */
export function withBrand(title: string): string {
  const flat = flatten(title);
  if (flat.toLowerCase().includes(SITE.name.toLowerCase())) return truncate(flat, TITLE_MAX);

  const suffix = ` · ${SITE.name}`;
  if (flat.length + suffix.length <= TITLE_MAX) return `${flat}${suffix}`;
  return truncate(flat, TITLE_MAX);
}

/** Ensures a fragment ends as a sentence, so descriptions can be concatenated. */
export function sentence(value: string): string {
  const flat = flatten(value);
  if (flat === '') return '';
  return /[.!?…]$/.test(flat) ? flat : `${flat}.`;
}

/** Joins sentences into one description and trims it to the readable length. */
export function describe(...parts: (string | undefined)[]): string {
  const joined = parts
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .map(sentence)
    .join(' ');
  return truncate(joined, DESCRIPTION_MAX);
}

/** "a, b and c" — an English list, for descriptions that name prerequisites. */
export function conjoin(items: string[], conjunction = 'and'): string {
  const clean = items.filter((item) => item.trim() !== '');
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0] ?? '';
  const head = clean.slice(0, -1).join(', ');
  return `${head} ${conjunction} ${clean[clean.length - 1]}`;
}

/** Minutes as an ISO 8601 duration, which is what schema.org asks for. */
export function isoDuration(minutes: number): string {
  const safe = Math.max(1, Math.round(minutes));
  if (safe < 60) return `PT${safe}M`;
  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  return rest === 0 ? `PT${hours}H` : `PT${hours}H${rest}M`;
}
